import { AnnotationTextInput } from "./AnnotationTextInput";
import { isActivityLabel, activityLevel } from "../player/activityLabels";
import { useMemo, useState } from "react";
import type { Annotation, Ontology, ReviewStatus } from "../api/types";
import { Badge, EmptyState, Field } from "./ui";
import { formatTimecode } from "../player/time";

/**
 * The annotation list and editor.
 *
 * Model output and human annotation are separated everywhere: different colour,
 * different lane on the timeline, and a model annotation carries accept/reject
 * controls that a manual one does not. Editing a model annotation marks it
 * `edited` rather than silently turning it into a human one — the provenance
 * survives the edit.
 */
export function AnnotationInspector({
  annotations,
  ontology,
  selectedId,
  onSelect,
  onUpdate,
  onDelete,
  onSeek,
  masterNs,
  onAddPoint,
  onAddInterval,
  exportUrl,
  pendingRange,
}: {
  annotations: Annotation[];
  ontology: Ontology | null;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, patch: Partial<Annotation>) => Promise<void>;
  onDelete: (id: string) => void;
  onSeek: (ns: number) => void;
  masterNs: number;
  onAddPoint: () => void;
  onAddInterval: () => void;
  exportUrl: string;
  pendingRange: [number, number] | null;
}) {
  const [error, setError] = useState("");
  const update = (id:string,patch:Partial<Annotation>) => void onUpdate(id,patch).catch(e => setError(e.message));
  const [filter, setFilter] = useState<"activities" | "all" | "manual" | "model">("activities");
  const selected = annotations.find((a) => a.id === selectedId) ?? null;

  const visible = useMemo(
    () =>
      annotations
        .filter((a) => filter === "all" || (filter === "activities" ? isActivityLabel(a) : filter === "model" ? a.source === "model" : a.source !== "model"))
        .sort((a, b) => a.start_ns - b.start_ns),
    [annotations, filter],
  );

  const kinds = ontology?.kinds ?? [];
  const kindOf = (id: string) => kinds.find((k) => k.id === id);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div className="row" style={{ padding: "8px 10px", borderBottom: "1px solid var(--line)", gap: 6 }}>
        <button onClick={onAddPoint} title="Add a point annotation at the playhead (M)">+ Point</button>
        <button onClick={onAddInterval} title={pendingRange ? "Create an interval from the marked range (Enter)" : "Mark a range with Alt+drag on the timeline, or set In/Out with I and O"}>
          + Interval
        </button>
        <span className="spacer" />
        <a href={exportUrl} download className="tiny">Export JSONL</a>
      </div>

      <div className="row" style={{ padding: "6px 10px", gap: 4 }}>
        {(["activities", "all", "manual", "model"] as const).map((f) => (
          <button key={f} className={filter === f ? "active" : "ghost"} onClick={() => setFilter(f)}>
            {f}
          </button>
        ))}
        <span className="spacer" />
        <span className="tiny muted">{visible.length}</span>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", borderBottom: "1px solid var(--line)" }}>
        {visible.length === 0 ? (
          <EmptyState title="No annotations yet">
            Press <kbd>M</kbd> to mark the current instant, or hold <kbd>Alt</kbd> and drag on the timeline to
            mark a range.
          </EmptyState>
        ) : (
          <table className="data">
            <tbody>
              {visible.map((a) => {
                const k = kindOf(a.kind);
                return (
                  <tr
                    key={a.id}
                    className={selectedId === a.id ? "selected" : ""}
                    onClick={() => { onSelect(a.id); onSeek(a.start_ns); }}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ width: 8, padding: 0, background: k?.color ?? "var(--manual)" }} />
                    <td>
                      <div className="row" style={{ gap: 5 }}>
                        <span style={{ fontWeight: 600 }}>{a.label || k?.label || a.kind}</span>
                        <Badge kind={a.source}>{a.source}</Badge>
                        {a.review_status !== "unreviewed" ? <Badge kind={a.review_status === "rejected" ? "invalid" : "good"}>{a.review_status}</Badge> : null}
                      </div>
                      <div className="tiny muted mono">
                        {formatTimecode(a.start_ns)}
                        {a.end_ns != null ? ` → ${formatTimecode(a.end_ns)} (${((a.end_ns - a.start_ns) / 1e9).toFixed(2)}s)` : ""}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {error && <div className="hint" role="alert">{error}</div>}
      <div style={{ padding: 10, maxHeight: "45%", overflow: "auto" }}>
        {!selected ? (
          <div className="hint">Select an annotation to edit it.</div>
        ) : (
          <>
            <Field label="Chapter level">
              <select aria-label="Selected chapter level" value={activityLevel(selected)} onChange={e=>update(selected.id,{payload:{...selected.payload,type:"activity",level:e.target.value}})}>
                <option value="context">Context</option><option value="activity">Activity</option>
              </select>
            </Field>
            <Field label="Kind">
              <select value={selected.kind} onChange={(e) => update(selected.id, { kind: e.target.value })}>
                {kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                {kindOf(selected.kind) ? null : <option value={selected.kind}>{selected.kind}</option>}
              </select>
            </Field>
            <Field label="Label" hint={kindOf(selected.kind)?.description}>
              <AnnotationTextInput key={selected.id} aria-label="Selected annotation label"
                list={`labels-${selected.kind}`} value={selected.label}
                onCommit={label => onUpdate(selected.id,{label})} placeholder="Describe the task" />
              <datalist id={`labels-${selected.kind}`}>
                {(kindOf(selected.kind)?.labels ?? []).map((l) => <option key={l} value={l} />)}
              </datalist>
            </Field>
            <div className="row" style={{ gap: 6, marginBottom: 10 }}>
              <button disabled={selected.end_ns != null && masterNs >= selected.end_ns} onClick={() => update(selected.id, { start_ns: masterNs })} title="Move the start to the playhead">
                Set start
              </button>
              <button
                onClick={() => update(selected.id, { end_ns: masterNs })}
                disabled={selected.end_ns == null || masterNs <= selected.start_ns}
                title={selected.end_ns == null ? "This is a point annotation" : "Move the end to the playhead"}
              >
                Set end
              </button>
              <button onClick={() => onSeek(selected.start_ns)}>Go to</button>
            </div>

            {Array.isArray(selected.payload?.evidence) && selected.payload.evidence.length > 0 && <details style={{marginBottom:10}}>
              <summary>Visual evidence · {selected.payload.evidence.length} windows</summary>
              <p className="tiny muted">Sampled observations support this proposal. Adjust its boundaries on the timeline.</p>
              {selected.payload.evidence.map((e:any,i:number)=><div key={i} style={{marginBottom:8}}>
                <button className="ghost tiny" onClick={()=>onSeek(e.sample_ns?.[0] ?? e.start_ns)}>{formatTimecode(e.start_ns)}–{formatTimecode(e.end_ns)}</button>
                <div className="tiny">{String(e.text ?? "")}</div>
              </div>)}
            </details>}
            {selected.source === "model" ? (
              <div style={{ marginBottom: 10 }}>
                <label>Review</label>
                <div className="row" style={{ gap: 6 }}>
                  {(["accepted", "rejected"] as ReviewStatus[]).map((s) => (
                    <button
                      key={s}
                      className={selected.review_status === s ? "active" : ""}
                      onClick={() => update(selected.id, { review_status: s })}
                    >
                      {s === "accepted" ? "Accept" : "Reject"}
                    </button>
                  ))}
                </div>
                <div className="tiny muted" style={{ marginTop: 5 }}>
                  {selected.confidence != null ? `model confidence ${(selected.confidence * 100).toFixed(0)}% · ` : ""}
                  {selected.provenance?.engine === "local_vlm" ? "Local visual-language proposal · Qwen3-VL" : "Automatic evidence proposal"}
                  <details><summary>Provenance</summary><pre style={{whiteSpace:"pre-wrap",overflowWrap:"anywhere"}}>{JSON.stringify(selected.provenance,null,2)}</pre></details>
                </div>
              </div>
            ) : null}

            <div className="row">
              <button className="danger" onClick={() => onDelete(selected.id)}>Delete</button>
              <span className="spacer" />
              <span className="tiny muted mono">{selected.id}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
